/**
 * Cell Access Analytics View
 * Focused on cell access statistics: request volumes, trends, popular cells, user activity.
 * Scraping detection has been moved to AnalyticsScrapingView.
 */
import * as echarts from 'echarts'
import { useEffect, useRef, useState } from 'preact/hooks'
import {
  getCellAccessAnalytics,
  getCellAccessOverview,
} from '../../../services/adminService.js'
import { COLORS, chartTheme } from './shared/chartTheme.js'
import {
  BlockedCount,
  RefusedBadge,
  TierBadge,
  UserDetailPanel,
} from './shared/cellAccessCharts.jsx'
import { formatNumber } from './shared/formatters.js'
import { Icons } from './shared/Icons.jsx'
import { KPICard } from './shared/KPICard.jsx'

/**
 * Daily trend line chart with full/batch/light requests and unique users
 */
function DailyTrendChart({ data, height = 260 }) {
  const chartRef = useRef(null)
  const chartInstance = useRef(null)

  useEffect(() => {
    if (!chartRef.current || !data || data.length === 0) return

    if (!chartInstance.current) {
      chartInstance.current = echarts.init(chartRef.current, null, { renderer: 'canvas' })
    }

    const option = {
      ...chartTheme,
      grid: { left: 50, right: 50, top: 30, bottom: 30 },
      tooltip: { ...chartTheme.tooltip, trigger: 'axis' },
      legend: { ...chartTheme.legend, right: 0, top: 0, data: ['Full Requests', 'Batch Curves', 'Light Requests', 'Unique Users'] },
      xAxis: {
        type: 'category',
        data: data.map((d) => d.date),
        axisLine: { lineStyle: { color: COLORS.grid } },
        axisLabel: { color: COLORS.gray, fontSize: 10, rotate: 45 },
      },
      yAxis: [
        {
          type: 'value',
          name: 'Requests',
          axisLabel: { color: COLORS.gray, fontSize: 11 },
          splitLine: { lineStyle: { color: COLORS.grid } },
        },
        {
          type: 'value',
          name: 'Users',
          axisLabel: { color: COLORS.gray, fontSize: 11 },
          splitLine: { show: false },
        },
      ],
      series: [
        {
          name: 'Full Requests',
          type: 'line',
          data: data.map((d) => d.full_requests),
          smooth: true,
          itemStyle: { color: COLORS.orange },
          areaStyle: { color: COLORS.orange + '20' },
        },
        {
          name: 'Batch Curves',
          type: 'line',
          data: data.map((d) => d.batch_curve_requests || 0),
          smooth: true,
          itemStyle: { color: '#8b5cf6' },
          areaStyle: { color: '#8b5cf620' },
        },
        {
          name: 'Light Requests',
          type: 'line',
          data: data.map((d) => d.light_requests),
          smooth: true,
          itemStyle: { color: COLORS.cyan },
          areaStyle: { color: COLORS.cyan + '20' },
        },
        {
          name: 'Unique Users',
          type: 'line',
          yAxisIndex: 1,
          data: data.map((d) => d.unique_users),
          smooth: true,
          itemStyle: { color: '#22c55e' },
          lineStyle: { type: 'dashed' },
        },
      ],
    }

    chartInstance.current.setOption(option)
    const handleResize = () => chartInstance.current?.resize()
    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [data])

  useEffect(() => () => { chartInstance.current?.dispose(); chartInstance.current = null }, [])

  if (!data || data.length === 0) return null

  return (
    <div className="analytics-chart-container">
      <div className="analytics-chart-header">
        <span className="analytics-chart-title">Daily Trend</span>
      </div>
      <div ref={chartRef} style={{ width: '100%', height }} />
    </div>
  )
}

/**
 * Weekly heatmap: 7 rows (Mon-Sun) x 24 cols (hours UTC)
 */
function WeeklyHeatmap({ data, height = 240 }) {
  const chartRef = useRef(null)
  const chartInstance = useRef(null)

  useEffect(() => {
    if (!chartRef.current || !data || data.length === 0) return

    if (!chartInstance.current) {
      chartInstance.current = echarts.init(chartRef.current, null, { renderer: 'canvas' })
    }

    const days = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun']
    const hours = Array.from({ length: 24 }, (_, i) => `${i}h`)
    const values = data.map((d) => [d.hour, d.day, d.count])
    const maxCount = Math.max(...data.map((d) => d.count), 1)

    const option = {
      ...chartTheme,
      grid: { left: 50, right: 30, top: 10, bottom: 30 },
      tooltip: {
        ...chartTheme.tooltip,
        formatter: (p) => `${days[p.value[1]]} ${p.value[0]}:00 UTC<br/>Requests: <strong>${p.value[2]}</strong>`,
      },
      xAxis: {
        type: 'category',
        data: hours,
        axisLabel: { color: COLORS.gray, fontSize: 10 },
        splitArea: { show: true, areaStyle: { color: ['transparent', 'rgba(255,255,255,0.02)'] } },
      },
      yAxis: {
        type: 'category',
        data: days,
        axisLabel: { color: COLORS.gray, fontSize: 11 },
      },
      visualMap: {
        min: 0,
        max: maxCount,
        calculable: false,
        orient: 'horizontal',
        left: 'center',
        bottom: 0,
        show: false,
        inRange: {
          color: ['rgba(234,91,12,0.05)', 'rgba(234,91,12,0.3)', 'rgba(234,91,12,0.6)', '#ea5b0c'],
        },
      },
      series: [{
        type: 'heatmap',
        data: values,
        label: { show: false },
        emphasis: { itemStyle: { shadowBlur: 10, shadowColor: 'rgba(0, 0, 0, 0.5)' } },
      }],
    }

    chartInstance.current.setOption(option)
    const handleResize = () => chartInstance.current?.resize()
    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [data])

  useEffect(() => () => { chartInstance.current?.dispose(); chartInstance.current = null }, [])

  if (!data || data.length === 0) return null

  return (
    <div className="analytics-chart-container">
      <div className="analytics-chart-header">
        <span className="analytics-chart-title">Activity Heatmap (UTC)</span>
      </div>
      <div ref={chartRef} style={{ width: '100%', height }} />
    </div>
  )
}

/**
 * Top accessed cells horizontal bar chart
 */
function TopCellsChart({ data, height = 300 }) {
  const chartRef = useRef(null)
  const chartInstance = useRef(null)

  useEffect(() => {
    if (!chartRef.current || !data || data.length === 0) return

    if (!chartInstance.current) {
      chartInstance.current = echarts.init(chartRef.current, null, { renderer: 'canvas' })
    }

    const sorted = [...data].sort((a, b) => a.access_count - b.access_count)
    const maxUsers = Math.max(...sorted.map((d) => d.unique_users), 1)

    const option = {
      ...chartTheme,
      grid: { left: 220, right: 30, top: 10, bottom: 20 },
      tooltip: {
        ...chartTheme.tooltip,
        trigger: 'axis',
        axisPointer: { type: 'shadow' },
        formatter: (params) => {
          const d = sorted[params[0].dataIndex]
          return `${d.cell_name || d.product_id}<br/>Requests: ${d.access_count}<br/>Unique Users: ${d.unique_users}`
        },
      },
      xAxis: {
        type: 'value',
        axisLabel: { color: COLORS.gray },
        splitLine: { lineStyle: { color: COLORS.grid } },
      },
      yAxis: {
        type: 'category',
        data: sorted.map((d) => {
          const name = d.cell_name || d.product_id
          return name.length > 28 ? name.substring(0, 28) + '...' : name
        }),
        axisLabel: { color: COLORS.gray, fontSize: 10 },
      },
      series: [{
        type: 'bar',
        data: sorted.map((d) => ({
          value: d.access_count,
          itemStyle: {
            color: d.unique_users <= 2 ? COLORS.orange : COLORS.cyan,
          },
        })),
        barWidth: '60%',
      }],
    }

    chartInstance.current.setOption(option)
    const handleResize = () => chartInstance.current?.resize()
    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [data])

  useEffect(() => () => { chartInstance.current?.dispose(); chartInstance.current = null }, [])

  if (!data || data.length === 0) return null

  return (
    <div className="analytics-chart-container">
      <div className="analytics-chart-header">
        <span className="analytics-chart-title">Top Accessed Cells</span>
        <span style={{ fontSize: '11px', color: COLORS.gray }}>
          <span style={{ color: COLORS.orange }}>&#9632;</span> Few users
          &nbsp;<span style={{ color: COLORS.cyan }}>&#9632;</span> Many users
        </span>
      </div>
      <div ref={chartRef} style={{ width: '100%', height }} />
    </div>
  )
}

/**
 * Main Cell Access Analytics View — focused on access statistics
 */
export function AnalyticsCellAccessView({ periodDays }) {
  const [data, setData] = useState(null)
  const [overview, setOverview] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [sortBy, setSortBy] = useState('full_cell_requests')
  const [sortDir, setSortDir] = useState('desc')
  const [expandedUser, setExpandedUser] = useState(null)

  useEffect(() => {
    let cancelled = false
    setLoading(true)
    setError(null)

    Promise.all([
      getCellAccessAnalytics({ period_days: periodDays, limit: 100 }),
      getCellAccessOverview(periodDays).catch(() => null),
    ])
      .then(([result, overviewResult]) => {
        if (!cancelled) {
          setData(result)
          setOverview(overviewResult)
        }
      })
      .catch((err) => {
        if (!cancelled) setError(err.message)
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })

    return () => { cancelled = true }
  }, [periodDays])

  if (loading) {
    return (
      <div className="analytics-loading">
        <div className="spinner"></div>
        <p>Loading cell access data...</p>
      </div>
    )
  }

  if (error) {
    return (
      <div className="analytics-error">
        <span>{Icons.alertTriangle}</span>
        <p>{error}</p>
      </div>
    )
  }

  if (!data) return null

  const items = data.items || []

  // Summary stats
  const totalFullRequests = items.reduce((sum, u) => sum + (u.full_cell_requests || 0), 0)
  const totalBatchRequests = items.reduce((sum, u) => sum + (u.batch_curve_requests || 0), 0)
  const totalLightRequests = items.reduce((sum, u) => sum + (u.light_requests || 0), 0)
  // Attempt-vs-served split. Totals are computed client-side from the same rows
  // the table shows, so the KPI and the new columns can never disagree.
  const totalAttemptedRequests = items.reduce(
    (sum, u) => sum + (u.attempted_full_requests ?? u.full_cell_requests ?? 0),
    0
  )
  const totalBlockedRequests = items.reduce((sum, u) => sum + (u.blocked_full_requests || 0), 0)
  const uniqueUsers = items.length

  // Sort items. `attempted_full_requests` falls back to the served count so the
  // column sorts on exactly the number it renders (an older API response without
  // the attempt split must not sort every row to 0).
  const sortValue = (u, col) =>
    col === 'attempted_full_requests'
      ? u.attempted_full_requests ?? u.full_cell_requests ?? 0
      : u[col] ?? 0

  const sorted = [...items].sort((a, b) => {
    const aVal = sortValue(a, sortBy)
    const bVal = sortValue(b, sortBy)
    if (sortDir === 'asc') return aVal > bVal ? 1 : aVal < bVal ? -1 : 0
    return aVal < bVal ? 1 : aVal > bVal ? -1 : 0
  })

  const handleSort = (col) => {
    if (sortBy === col) {
      setSortDir(sortDir === 'asc' ? 'desc' : 'asc')
    } else {
      setSortBy(col)
      setSortDir('desc')
    }
  }

  const sortIndicator = (col) => {
    if (sortBy !== col) return ''
    return sortDir === 'asc' ? ' \u25B2' : ' \u25BC'
  }

  return (
    <div className="analytics-cell-access-view">
      {/* Summary KPIs */}
      <div className="analytics-kpi-grid">
        <KPICard
          icon={Icons.zap}
          label="Full Data Requests"
          value={formatNumber(totalFullRequests)}
          color="orange"
        />
        <KPICard
          icon={Icons.alertTriangle}
          label="Blocked Requests"
          value={formatNumber(totalBlockedRequests)}
          sublabel={`of ${formatNumber(totalAttemptedRequests)} attempted`}
          color={totalBlockedRequests > 0 ? 'red' : 'green'}
        />
        <KPICard
          icon={Icons.zap}
          label="Batch Curve Requests"
          value={formatNumber(totalBatchRequests)}
          color="purple"
        />
        <KPICard
          icon={Icons.zap}
          label="Light Requests"
          value={formatNumber(totalLightRequests)}
          color="cyan"
        />
        <KPICard
          icon={Icons.users}
          label="Unique Users"
          value={formatNumber(uniqueUsers)}
          sublabel={`of ${formatNumber(data.total)} total`}
          color="cyan"
        />
      </div>

      {/* Overview charts */}
      {overview && (
        <div>
          {/* Daily Trend */}
          <div style={{ marginBottom: '16px' }}>
            <DailyTrendChart data={overview.daily_trend} />
          </div>

          {/* Heatmap + Top Cells */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '16px' }}>
            <WeeklyHeatmap data={overview.hourly_heatmap} />
            {overview.top_accessed_cells && overview.top_accessed_cells.length > 0 && (
              <TopCellsChart data={overview.top_accessed_cells} />
            )}
          </div>
        </div>
      )}

      {/* User table — access focused */}
      <div className="analytics-table-section">
        <h3>Cell Data Access by User</h3>
        <div style={{ overflowX: 'auto' }}>
          <table className="analytics-table cell-access-table">
            <thead>
              <tr>
                <th className="sortable-th" onClick={() => handleSort('email')}>
                  User{sortIndicator('email')}
                </th>
                <th>Tier</th>
                <th
                  className="sortable-th count-cell"
                  onClick={() => handleSort('full_cell_requests')}
                  title="Full cell-data requests that were actually SERVED (2xx/3xx)."
                >
                  Full Requests{sortIndicator('full_cell_requests')}
                </th>
                <th
                  className="sortable-th count-cell"
                  onClick={() => handleSort('attempted_full_requests')}
                  title="Full cell-data requests ATTEMPTED, served or not. Always ≥ Full Requests."
                >
                  Attempted{sortIndicator('attempted_full_requests')}
                </th>
                <th
                  className="sortable-th count-cell"
                  onClick={() => handleSort('blocked_full_requests')}
                  title="Attempted full-data requests that were refused (429 rate limit / 401 / 403)."
                >
                  Blocked{sortIndicator('blocked_full_requests')}
                </th>
                <th className="sortable-th count-cell" onClick={() => handleSort('batch_curve_requests')}>
                  Batch Curves{sortIndicator('batch_curve_requests')}
                </th>
                <th className="sortable-th count-cell" onClick={() => handleSort('unique_cells_accessed')}>
                  Unique Cells{sortIndicator('unique_cells_accessed')}
                </th>
                <th className="sortable-th count-cell" onClick={() => handleSort('light_requests')}>
                  Light Requests{sortIndicator('light_requests')}
                </th>
                <th className="sortable-th count-cell" onClick={() => handleSort('active_days')}>
                  Active Days{sortIndicator('active_days')}
                </th>
                <th
                  className="sortable-th"
                  onClick={() => handleSort('first_access')}
                  title="Date of the user's first recorded request in the period."
                >
                  First Active{sortIndicator('first_access')}
                </th>
                <th
                  className="sortable-th count-cell"
                  onClick={() => handleSort('day_spread_days')}
                  title="Days between first and last active day (last − first)."
                >
                  Day Spread{sortIndicator('day_spread_days')}
                </th>
                <th
                  className="sortable-th count-cell"
                  onClick={() => handleSort('cumulative_heartbeat_minutes')}
                  title="Estimated active time: sum of gaps between consecutive requests under a 30-min inactivity timeout."
                >
                  Heartbeat (min){sortIndicator('cumulative_heartbeat_minutes')}
                </th>
              </tr>
            </thead>
            <tbody>
              {sorted.map((user) => {
                const isExpanded = expandedUser === user.user_id
                return [
                  <tr
                    key={user.user_id}
                    className={`cell-access-row ${isExpanded ? 'cell-access-row--expanded' : ''}`}
                    onClick={() => setExpandedUser(isExpanded ? null : user.user_id)}
                    style={{ cursor: 'pointer' }}
                  >
                    <td>
                      <div className="cell-access-user-info">
                        <span className="cell-access-user-name">
                          {user.full_name || 'Unknown'}
                        </span>
                        <span className="cell-access-user-email">{user.email}</span>
                        {user.company && (
                          <span className="cell-access-user-company">{user.company}</span>
                        )}
                        <RefusedBadge
                          attempted={user.attempted_full_requests}
                          blocked={user.blocked_full_requests}
                        />
                      </div>
                    </td>
                    <td><TierBadge tier={user.subscription_tier} /></td>
                    <td className="count-cell">{formatNumber(user.full_cell_requests)}</td>
                    <td className="count-cell">
                      {formatNumber(user.attempted_full_requests ?? user.full_cell_requests ?? 0)}
                    </td>
                    <td className="count-cell">
                      <BlockedCount
                        attempted={user.attempted_full_requests}
                        blocked={user.blocked_full_requests}
                      />
                    </td>
                    <td className="count-cell">{formatNumber(user.batch_curve_requests || 0)}</td>
                    <td className="count-cell">{formatNumber(user.unique_cells_accessed)}</td>
                    <td className="count-cell">{formatNumber(user.light_requests)}</td>
                    <td className="count-cell">{user.active_days}</td>
                    <td>
                      {user.first_access
                        ? new Date(user.first_access).toLocaleDateString('en-GB')
                        : '–'}
                    </td>
                    <td className="count-cell">
                      {user.day_spread_days != null ? `${user.day_spread_days}d` : '–'}
                    </td>
                    <td className="count-cell">
                      {formatNumber(user.cumulative_heartbeat_minutes ?? 0)}
                    </td>
                  </tr>,
                  isExpanded && (
                    <tr key={`${user.user_id}-detail`} className="cell-access-detail-row">
                      <td colSpan={12}>
                        <UserDetailPanel userId={user.user_id} periodDays={periodDays} />
                      </td>
                    </tr>
                  ),
                ]
              })}
              {sorted.length === 0 && (
                <tr>
                  <td colSpan={12} style={{ textAlign: 'center', padding: '24px' }}>
                    No data in the selected period
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  )
}
